import React, { useContext, useEffect, useMemo, useState } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.bubble.css'

import { CardContent, CardFooter } from '@components/layout/Card'
import { Button } from '@components/Button'
import useFeatureConfig, { FeatureNames } from '@hooks/useFeatureConfig'
import { getTimestamp } from '@utils/dateParser'

import { NotesContext } from '../../context'

const baseClass = 'scenario-notes__import'

// Mirrors the server's duplicate check (ImportNotes#fingerprint), including
// the author's polymorphic identity. A note already on this plan is shown as such and
// left unticked; the server skips it regardless.
const fingerprint = (note) =>
  JSON.stringify([note.author_type, note.author_id, Number(note.visibility), note.body, note.created_at])

const sourceLabel = (source) =>
  `${source.name || 'My plan'} (${source.planType.replace('_', ' ')})`

export const Import = () => {
  const [{ isEnabled: viewPrivateNotesEnabled }] = useFeatureConfig(FeatureNames.NOTE__VIEW_PRIVATE)
  const { state: { notes, crudActions }, actions: { back } } = useContext(NotesContext)

  const [sources, setSources] = useState(null)
  const [sourceKey, setSourceKey] = useState('')
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    crudActions.importSources()
      .then(setSources)
      .catch((error) => { setSources([]); setMessage(error.message) })
  }, [])

  const existing = useMemo(() => new Set(notes.map(fingerprint)), [notes])
  const source = sources?.find((s) => `${s.type}-${s.id}` === sourceKey)
  const sourceNotes = (source?.notes || []).filter((note) => viewPrivateNotesEnabled() || note.visibility == 0)
  const importable = sourceNotes.filter((note) => !existing.has(fingerprint(note)))

  const selectSource = (key) => {
    const next = sources.find((s) => `${s.type}-${s.id}` === key)
    setSourceKey(key)
    setMessage(null)
    setSelectedIds(new Set(
      (next?.notes || [])
        .filter((note) => (viewPrivateNotesEnabled() || note.visibility == 0) && !existing.has(fingerprint(note)))
        .map((note) => note.id)
    ))
  }

  const toggle = (id) => {
    const next = new Set(selectedIds)
    next.has(id) ? next.delete(id) : next.add(id)
    setSelectedIds(next)
  }

  const onImport = async () => {
    setSubmitting(true)
    try {
      const { imported, skipped } = await crudActions.import({
        source_type: source.type,
        source_id: source.id,
        note_ids: [...selectedIds]
      })
      setSelectedIds(new Set())
      setMessage(`Imported ${imported} ${imported === 1 ? 'note' : 'notes'}` +
        (skipped ? `, skipped ${skipped} already on this plan.` : '.'))
    } catch (error) {
      setMessage(error.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <CardContent className={baseClass}>
        <label htmlFor={`${baseClass}-source`}><b>Import notes from</b></label>
        <select
          id={`${baseClass}-source`}
          value={sourceKey}
          disabled={!sources || submitting}
          onChange={(e) => selectSource(e.target.value)}
        >
          <option value=''>{sources ? 'Select a plan' : 'Loading plans...'}</option>
          {sources?.map((s) => (
            <option key={`${s.type}-${s.id}`} value={`${s.type}-${s.id}`}>{sourceLabel(s)}</option>
          ))}
        </select>
        {sources?.length === 0 && !message && <p>No other plans for this client have notes.</p>}
        {message && <p role='status'>{message}</p>}

        {source && (
          <>
            <div className={`${baseClass}-selection`}>
              <button type='button' disabled={submitting} onClick={() => setSelectedIds(new Set(importable.map((n) => n.id)))}>Select all</button>
              <button type='button' disabled={submitting} onClick={() => setSelectedIds(new Set())}>Clear all</button>
            </div>
            <div className='scenario-notes__notes-overflow-container'>
              {sourceNotes.map((note) => {
                const present = existing.has(fingerprint(note))
                return (
                  <label key={note.id} className='scenario-notes__note'>
                    <div className='note-header'>
                      <input
                        type='checkbox'
                        checked={selectedIds.has(note.id)}
                        disabled={present || submitting}
                        onChange={() => toggle(note.id)}
                      />
                      <b> {note.authorName}</b>
                      {' · '}{note.visibility == 1 ? 'Private' : 'Shared'}
                      {' · '}{getTimestamp(note.updated_at)}
                      {present && <em> · Already on this plan</em>}
                    </div>
                    <div className='note-quill'>
                      <ReactQuill readOnly theme='bubble' value={note.body} />
                    </div>
                  </label>
                )
              })}
            </div>
          </>
        )}
      </CardContent>
      <CardFooter>
        <div className='scenario-notes__create-actions'>
          <Button fullWidth secondary onClick={back} disabled={submitting}>Back</Button>
          <Button
            fullWidth
            primary
            trackAs='Import scenario notes'
            disabled={!source || selectedIds.size === 0 || submitting}
            onClick={onImport}
          >
            {submitting ? 'Importing...' : `Import ${selectedIds.size || ''} ${selectedIds.size === 1 ? 'note' : 'notes'}`}
          </Button>
        </div>
      </CardFooter>
    </>
  )
}
